import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, FileText, Activity, Users, Wallet, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { useSettings } from "../lib/settings";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import DoctorMetrics from "../components/DoctorMetrics";

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const settings = useSettings();
  const [appts, setAppts] = useState([]);
  const [rxs, setRxs] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    api.get("/appointments").then(({ data }) => setAppts(data)).catch(() => {});
    api.get("/prescriptions").then(({ data }) => setRxs(data)).catch(() => {});
    if (user?.role === "admin") api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  }, [user]);

  const today = new Date().toISOString().slice(0, 10);
  const todays = appts.filter((a) => a.date === today);
  const upcoming = appts.filter((a) => a.date > today).slice(0, 5);
  const monthPrefix = today.slice(0, 7);
  const monthIncome = appts
    .filter((a) => a.payment_status === "paid" && (a.date || "").startsWith(monthPrefix))
    .reduce((s, a) => s + (a.price_eur || 20), 0);
  const pendingPayments = appts.filter((a) => a.payment_status === "pending" && a.status !== "cancelled").length;

  const stats = [
    { icon: CalendarDays, label: t("today"), value: todays.length },
    { icon: Activity, label: t("upcoming"), value: upcoming.length },
    { icon: FileText, label: t("prescriptions"), value: rxs.length },
    ...(user?.role === "doctor"
      ? [
          { icon: Wallet, label: t("income_month"), value: `${monthIncome}€` },
          { icon: AlertCircle, label: t("pending_payments"), value: pendingPayments },
        ]
      : []),
    ...(user?.role === "admin" ? [{ icon: Users, label: t("users"), value: users.length }] : []),
  ];

  return (
    <div className="space-y-8 fade-up" data-testid="dashboard-page">
      {(user?.role === "doctor" || user?.role === "admin") && <DoctorMetrics />}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <Card key={i} className="card-lift" data-testid={`stat-${i}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-muted-foreground">{s.label}</div>
                  <div className="font-display text-3xl mt-1">{s.value}</div>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#F3F1EC] flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-display">{t("upcoming")}</CardTitle>
            <Link to="/app/appointments"><Button size="sm" variant="outline" data-testid="go-appointments">{t("appointments")}</Button></Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {upcoming.length === 0 && <div className="text-sm text-muted-foreground">{t("no_data")}</div>}
            {upcoming.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <div className="text-sm font-medium">{a.date} · {a.time}</div>
                  <div className="text-xs text-muted-foreground">{user?.role === "patient" ? a.doctor_name : a.patient_name} — {a.reason}</div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-[#F3F1EC]">{t(a.status)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-display">{t("prescriptions")}</CardTitle>
            <Link to="/app/prescriptions"><Button size="sm" variant="outline" data-testid="go-prescriptions">{t("prescriptions")}</Button></Link>
          </CardHeader>
          <CardContent className="space-y-2">
            {rxs.slice(0, 5).map((r) => (
              <div key={r.id} className="flex items-center justify-between py-2 border-b last:border-0">
                <div>
                  <div className="text-sm font-medium">{r.code}</div>
                  <div className="text-xs text-muted-foreground">{r.diagnosis}</div>
                </div>
                <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            ))}
            {rxs.length === 0 && <div className="text-sm text-muted-foreground">{t("no_data")}</div>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
